"use client";
import { SelectionChip } from "@cmp-src/data-display/SelectionChip/SelectionChip";
import { UserAvatar } from "@cmp-src/data-display/UserAvatar/UserAvatar";
import { Button } from "@cmp-src/primitives/button";
import { AsyncUserPicker } from "@gettactic/components/src/controls/UserPicker/AsyncUserPicker";
import { Plus, Users } from "lucide-react";
import React from "react";
import type { ReleaseGrantee } from "@/features/reserve/hooks/useReleaseDedicatedDesk";
import { TeamPicker } from "@/features/teams/components/TeamPicker";

const TeamGlyph: React.FC = () => (
  <span className="flex h-5 w-5 items-center justify-center rounded-full bg-indigo-50 text-primary">
    <Users className="h-3 w-3" aria-hidden="true" />
  </span>
);

interface GranteeRowProps {
  label: string;
  emptyText: string;
  grantees: ReleaseGrantee[];
  renderIcon: (grantee: ReleaseGrantee) => React.ReactNode;
  onRemove: (id: string) => void;
  action: React.ReactNode;
}

const GranteeRow: React.FC<GranteeRowProps> = ({
  label,
  emptyText,
  grantees,
  renderIcon,
  onRemove,
  action
}) => (
  <div className="flex flex-col gap-2">
    <div className="flex items-center justify-between gap-2">
      <span className="font-medium text-neutral-800 text-sm">
        {label}
        {grantees.length > 0 ? (
          <span className="ml-1.5 font-normal text-neutral-400 text-xs">
            {grantees.length}
          </span>
        ) : null}
      </span>
      {action}
    </div>
    {grantees.length > 0 ? (
      <div className="flex flex-wrap gap-2">
        {grantees.map((grantee) => (
          <SelectionChip
            key={grantee.id}
            label={grantee.name}
            icon={renderIcon(grantee)}
            onRemove={() => onRemove(grantee.id)}
          />
        ))}
      </div>
    ) : (
      <p className="text-neutral-400 text-xs">{emptyText}</p>
    )}
  </div>
);

export interface ReleaseGranteesPickerProps {
  users: ReleaseGrantee[];
  teams: ReleaseGrantee[];
  onUsersChange: (users: ReleaseGrantee[]) => void;
  onTeamsChange: (teams: ReleaseGrantee[]) => void;
}

/**
 * Who may book a released desk: people and teams as chips, added through
 * the searching pickers the settings screens use.
 */
export const ReleaseGranteesPicker: React.FC<ReleaseGranteesPickerProps> = ({
  users,
  teams,
  onUsersChange,
  onTeamsChange
}) => {
  const addButton = (label: string) => (
    <Button
      type="button"
      variant="outline"
      size="sm"
      iconLeft={<Plus className="h-4 w-4" />}
    >
      {label}
    </Button>
  );

  return (
    <div className="flex flex-col gap-4 rounded-md border border-neutral-200 p-3">
      <GranteeRow
        label="People"
        emptyText="Nobody yet."
        grantees={users}
        renderIcon={(user) => <UserAvatar user={user} size="xs" />}
        onRemove={(id) => onUsersChange(users.filter((u) => u.id !== id))}
        action={
          <AsyncUserPicker
            excludeUserIds={users.map((u) => u.id)}
            onSelect={(user) =>
              onUsersChange([
                ...users,
                {
                  id: user.id,
                  name: user.name,
                  profile_photo_url: user.profile_photo_url
                }
              ])
            }
            trigger={addButton("Add person")}
          />
        }
      />
      <GranteeRow
        label="Teams"
        emptyText="No team yet."
        grantees={teams}
        renderIcon={() => <TeamGlyph />}
        onRemove={(id) => onTeamsChange(teams.filter((t) => t.id !== id))}
        action={
          <TeamPicker
            excludeTeamIds={teams.map((t) => t.id)}
            onSelect={(team) =>
              onTeamsChange([...teams, { id: team.id, name: team.name }])
            }
            trigger={addButton("Add team")}
          />
        }
      />
    </div>
  );
};
